/**
 * Lightweight password strength estimate for the S-04 meter (screens.md).
 * Display-only guidance — the server's zod min(10) is the enforcement point.
 * Dependency-free by design (no CDN, no zxcvbn bundle — ARCH §6 CSP).
 */

const COMMON = new Set([
  "password",
  "password1",
  "passw0rd",
  "qwertyuiop",
  "1234567890",
  "12345678910",
  "azertyuiop",
  "iloveyou12",
  "letmein123",
  "sunshine12",
  "monkey12345",
  "dragon12345",
]);

export interface StrengthResult {
  /** 0..4 (very weak .. very strong). */
  score: 0 | 1 | 2 | 3 | 4;
  label: string;
  /** true once the server minimum (10 chars) is met. */
  meetsMinimum: boolean;
}

export function estimateStrength(password: string): StrengthResult {
  const meetsMinimum = password.length >= 10;
  if (password.length === 0) return { score: 0, label: "—", meetsMinimum: false };
  if (COMMON.has(password.toLowerCase())) {
    return { score: 0, label: "Seen everywhere. Literally everywhere.", meetsMinimum };
  }
  let classes = 0;
  if (/[a-z]/.test(password)) classes += 1;
  if (/[A-Z]/.test(password)) classes += 1;
  if (/\d/.test(password)) classes += 1;
  if (/[^a-zA-Z\d]/.test(password)) classes += 1;
  // rough entropy: charset breadth × length, discounted for repeats
  const uniq = new Set(password).size;
  const effective = Math.min(password.length, uniq * 2);
  const bits = effective * Math.log2(Math.max(classes * 26, 26));
  let score: StrengthResult["score"];
  if (!meetsMinimum || bits < 40) score = 1;
  else if (bits < 60) score = 2;
  else if (bits < 80) score = 3;
  else score = 4;
  if (password.length < 6) score = 0;
  const labels: Record<number, string> = {
    0: "Instantly guessable",
    1: meetsMinimum ? "Weak" : "Too short (10 minimum)",
    2: "Okay",
    3: "Strong",
    4: "Excellent",
  };
  return { score, label: labels[score] ?? "—", meetsMinimum };
}

/** Disposable-domain soft-warn list (S-04 — warn, never block). */
const DISPOSABLE = new Set([
  "mailinator.com",
  "guerrillamail.com",
  "10minutemail.com",
  "tempmail.com",
  "temp-mail.org",
  "yopmail.com",
  "sharklasers.com",
  "trashmail.com",
  "getnada.com",
  "dispostable.com",
]);

export function isDisposableDomain(email: string): boolean {
  const at = email.lastIndexOf("@");
  if (at < 0) return false;
  return DISPOSABLE.has(email.slice(at + 1).toLowerCase());
}
